// Activo Fijo → Toma Física → Conciliación de toma física.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, GitCompareArrows, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { TomaDetalle, TomaRow, afRpc, fieldCls } from './shared';

export function ConciliacionTomaAF() {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const { data: tomas = [] } = useQuery<TomaRow[]>({ queryKey: ['af-tomas'], queryFn: () => afRpc<TomaRow[]>('af_tomas') });
  const [tid, setTid] = useState('');
  useEffect(() => { if (!tid && tomas.length) setTid(tomas[0].id); }, [tomas, tid]);
  const { data: det = [], isFetching } = useQuery<TomaDetalle[]>({ queryKey: ['af-toma-det', tid], queryFn: () => afRpc<TomaDetalle[]>('af_toma_detalle', { _toma_id: tid }), enabled: !!tid });

  const faltantes = det.filter((d) => d.estado === 'no_encontrado');
  const sobrantes = det.filter((d) => d.es_sobrante);
  const incorrectas = det.filter((d) => d.ubic_incorrecta);

  const corregir = useMutation({
    mutationFn: (id: string) => afRpc('af_toma_corregir', { _detalle_id: id }),
    onSuccess: () => { toast.success('Ubicación/custodio del activo corregido'); void qc.invalidateQueries({ queryKey: ['af-toma-det', tid] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const Tabla = ({ titulo, rows, accion }: { titulo: string; rows: TomaDetalle[]; accion?: (d: TomaDetalle) => React.ReactNode }) => (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{titulo} ({rows.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th><th className="py-2 pr-3">Ubic. esperada</th><th className="py-2 pr-3">Ubic. hallada</th><th className="py-2"></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground text-xs">Ninguno</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} className="border-b last:border-0">
                <td className="py-2 pr-3 font-mono">{d.placa}</td><td className="py-2 pr-3">{d.activo}</td>
                <td className="py-2 pr-3 text-muted-foreground">{d.ubic_esperada ?? '—'}</td>
                <td className="py-2 pr-3">{d.ubic_hallada ?? '—'}</td>
                <td className="py-2 text-right">{accion?.(d)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><GitCompareArrows className="w-5 h-5" /> Conciliación de toma física</h1>
      <div className="flex items-center gap-2">
        <select className={`${fieldCls} w-80`} value={tid} onChange={(e) => setTid(e.target.value)}>
          <option value="">— Selecciona una toma —</option>
          {tomas.map((t) => <option key={t.id} value={t.id}>{t.fecha} · {t.ubicacion ?? 'Toda la empresa'} · {t.estado}</option>)}
        </select>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      {tid && (
        <div className="space-y-4">
          <Tabla titulo="Ubicación / custodio distinto" rows={incorrectas} accion={(d) => (
            <Button size="sm" variant="outline" isLoading={corregir.isPending} onClick={() => corregir.mutate(d.id)}><MapPin className="w-4 h-4 mr-1" /> Corregir en el activo</Button>
          )} />
          <Tabla titulo="Faltantes (no encontrados)" rows={faltantes} accion={() => (
            <button className="inline-flex items-center gap-1 text-primary hover:underline text-sm" onClick={() => openTab({ id: '/activo-fijo/bajas', title: 'Bajas / Retiros', path: '/activo-fijo/bajas' })}>Dar de baja <ExternalLink className="w-3.5 h-3.5" /></button>
          )} />
          <Tabla titulo="Sobrantes (hallados fuera del alcance)" rows={sobrantes} />
        </div>
      )}
    </div>
  );
}
